<template>
  <div class="team-management">
    <!-- 页面标题和操作按钮 -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">战队管理</h2>
        <p class="page-subtitle">管理平台所有战队信息</p>
      </div>
      <div class="header-right">
        <el-button type="primary" @click="handleCreateTeam">
          <el-icon><Plus /></el-icon>
          创建战队
        </el-button>
      </div>
    </div>

    <!-- 筛选和搜索区域 -->
    <div class="filter-section">
      <el-card shadow="never" class="filter-card">
        <div class="filter-content">
          <div class="quick-filters">
            <el-radio-group v-model="filterParams.status" @change="handleFilterChange">
              <el-radio-button label="">全部战队</el-radio-button>
              <el-radio-button label="active">活跃战队</el-radio-button>
              <el-radio-button label="inactive">未激活</el-radio-button>
              <el-radio-button label="full">已满员</el-radio-button>
            </el-radio-group>
          </div>

          <div class="advanced-filters">
            <el-form :model="filterParams" inline>
              <el-form-item label="运动类型">
                <el-select v-model="filterParams.sportType" placeholder="选择运动类型" clearable @change="handleFilterChange">
                  <el-option label="篮球" value="basketball" />
                  <el-option label="足球" value="football" />
                  <el-option label="羽毛球" value="badminton" />
                  <el-option label="乒乓球" value="pingpong" />
                  <el-option label="网球" value="tennis" />
                </el-select>
              </el-form-item>
              <el-form-item label="创建时间">
                <el-date-picker v-model="filterParams.createDateRange" type="daterange" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" @change="handleFilterChange" value-format="YYYY-MM-DD" />
              </el-form-item>
              <el-form-item>
                <el-button @click="resetFilters">重置</el-button>
              </el-form-item>
            </el-form>
          </div>

          <div class="search-box">
            <el-input v-model="filterParams.keyword" placeholder="搜索战队名称、队长或描述" prefix-icon="Search" @input="handleSearch" clearable class="search-input" />
          </div>
        </div>
      </el-card>
    </div>

    <!-- 数据统计卡片 -->
    <div class="stats-section">
      <el-row :gutter="16">
        <el-col :xs="12" :sm="6" v-for="stat in teamStats" :key="stat.title">
          <el-card shadow="hover" class="stat-card">
            <div class="stat-content">
              <div class="stat-icon" :style="{ color: stat.color }">
                <el-icon><component :is="stat.icon" /></el-icon>
              </div>
              <div class="stat-info">
                <div class="stat-value">{{ stat.value }}</div>
                <div class="stat-title">{{ stat.title }}</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 战队列表 -->
    <div class="list-section">
      <el-card shadow="never">
        <template #header>
          <div class="table-header">
            <span>战队列表</span>
            <div class="table-actions">
              <el-tooltip content="刷新数据" placement="top">
                <el-button circle @click="loadTeams">
                  <el-icon><Refresh /></el-icon>
                </el-button>
              </el-tooltip>
            </div>
          </div>
        </template>

        <el-table :data="teamList" v-loading="loading" stripe style="width: 100%">
          <el-table-column type="expand">
            <template #default="{ row }">
              <div class="expand-content">
                <el-descriptions :column="2" border>
                  <el-descriptions-item label="战队ID">{{ row.id }}</el-descriptions-item>
                  <el-descriptions-item label="创建时间">{{ formatDateTime(row.createTime) }}</el-descriptions-item>
                  <el-descriptions-item label="最后活动">{{ formatDateTime(row.lastActivityTime) }}</el-descriptions-item>
                  <el-descriptions-item label="参赛次数">{{ row.activityCount }}</el-descriptions-item>
                  <el-descriptions-item label="奖金分配">
                    <el-tag size="small" :type="row.prize_distribution_method === 'average_all' ? 'success' : 'warning'">
                      {{ row.prize_distribution_method === 'average_all' ? '平均分配' : '队长收取' }}
                    </el-tag>
                  </el-descriptions-item>
                </el-descriptions>
                <div class="members-section">
                  <h4>战队成员 ({{ row.members.length }}/{{ row.maxMembers }})</h4>
                  <el-row :gutter="12">
                    <el-col :xs="12" :sm="8" :md="6" v-for="member in row.members" :key="member.id">
                      <div class="member-item">
                        <el-avatar :size="40" :src="member.avatar" />
                        <div class="member-info">
                          <div class="member-name">{{ member.nickname }}</div>
                          <div class="member-role">
                            <el-tag size="small" :type="(member.role === 'captain' || member.role === 'owner') ? 'danger' : 'info'">
                              {{ (member.role === 'captain' || member.role === 'owner') ? '队长' : '队员' }}
                            </el-tag>
                          </div>
                        </div>
                      </div>
                    </el-col>
                  </el-row>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="战队信息" min-width="200">
            <template #default="{ row }">
              <div class="team-info">
                <el-avatar :size="50" :src="row.avatar || row.logo" shape="square" />
                <div class="team-details">
                  <div class="team-name">{{ row.name }}</div>
                  <div class="team-desc">{{ row.description }}</div>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="sportType" label="运动类型" width="120">
            <template #default="{ row }">
              <el-tag :type="getSportTypeTag(row.sportType)">
                {{ getSportTypeLabel(row.sportType) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="成员数量" width="120">
            <template #default="{ row }">
              <div class="member-count">
                <span :class="{ 'text-danger': row.members.length >= row.maxMembers }">
                  {{ row.members.length }}/{{ row.maxMembers }}
                </span>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="leader.nickname" label="队长" width="120" />

          <el-table-column label="参赛次数" width="100" sortable :sort-method="(a, b) => (a.activityCount || 0) - (b.activityCount || 0)">
            <template #default="{ row }">
              <span>{{ row.activityCount || 0 }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="status" label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="getStatusTagType(row.status)" effect="light">
                {{ getStatusLabel(row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="200" fixed="right">
            <template #default="{ row }">
              <el-button size="small" @click="handleViewTeam(row)">详情</el-button>
              <el-dropdown>
                <el-button size="small">
                  更多<el-icon><ArrowDown /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item @click="handleEditTeam(row)">
                      <el-icon><Edit /></el-icon>编辑
                    </el-dropdown-item>
                    <el-dropdown-item @click="handleToggleStatus(row)" :disabled="row.status === 'inactive'">
                      <el-icon><Switch /></el-icon>{{ row.status === 'active' ? '禁用' : '启用' }}
                    </el-dropdown-item>
                    <el-dropdown-item @click="handleManageMembers(row)">
                      <el-icon><User /></el-icon>管理成员
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-section">
          <el-pagination
            v-model:current-page="pagination.current"
            v-model:page-size="pagination.size"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 创建/编辑战队弹窗 -->
    <el-dialog :title="dialogTitle" v-model="dialogVisible" width="600px" :before-close="handleDialogClose">
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="100px">
        <el-form-item label="战队名称" prop="name">
          <el-input v-model="formData.name" placeholder="请输入战队名称" />
        </el-form-item>
        <el-form-item label="运动类型" prop="sportType">
          <el-select v-model="formData.sportType" placeholder="请选择运动类型">
            <el-option label="篮球" value="basketball" />
            <el-option label="足球" value="football" />
            <el-option label="羽毛球" value="badminton" />
            <el-option label="乒乓球" value="pingpong" />
            <el-option label="网球" value="tennis" />
          </el-select>
        </el-form-item>
        <el-form-item label="最大人数" prop="maxMembers">
          <el-input-number v-model="formData.maxMembers" :min="5" :max="50" placeholder="请输入最大成员数量" />
        </el-form-item>
        <el-form-item label="奖金分配策略">
          <el-select v-model="formData.prize_distribution_method" placeholder="选择奖金分配方式">
            <el-option label="队长收取" value="captain_only" />
            <el-option label="平均分配" value="average_all" />
          </el-select>
        </el-form-item>
        <el-form-item label="战队描述" prop="description">
          <el-input v-model="formData.description" type="textarea" :rows="3" placeholder="请输入战队描述" />
        </el-form-item>
        <el-form-item label="战队头像" prop="avatar">
          <div style="display: flex; align-items: center; gap: 12px;">
            <el-avatar v-if="formData.avatar || formData.logo" :size="60" :src="formData.avatar || formData.logo" shape="square" />
            <el-upload action="#" :auto-upload="false" :show-file-list="false" :on-change="handleAvatarChange">
              <el-button type="primary">选择图片</el-button>
            </el-upload>
          </div>
          <div class="el-upload__tip" style="color: #909399; font-size: 12px; margin-top: 4px;">建议上传 200x200 像素的图片</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确认</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 成员管理弹窗 -->
    <el-dialog :title="`成员管理 - ${memberTeam.name}`" v-model="memberDialogVisible" width="700px">
      <div class="member-management">
        <div class="member-header">
          <span>成员列表 ({{ memberList.length }}/{{ memberTeam.maxMembers || 12 }})</span>
        </div>
        <el-table :data="memberList" v-loading="memberLoading" stripe style="width: 100%">
          <el-table-column label="成员" min-width="200">
            <template #default="{ row }">
              <div style="display: flex; align-items: center; gap: 10px;">
                <el-avatar :size="36" :src="row.avatar" />
                <span>{{ row.nickname }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="role" label="角色" width="100">
            <template #default="{ row }">
              <el-tag :type="row.role === 'captain' || row.role === 'owner' ? 'danger' : 'info'" size="small">
                {{ row.role === 'captain' || row.role === 'owner' ? '队长' : row.role === 'admin' ? '管理员' : '队员' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="加入时间" width="170">
            <template #default="{ row }">
              {{ row.joinTime ? row.joinTime.replace('T', ' ').substring(0, 19) : '-' }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80" fixed="right">
            <template #default="{ row }">
              <el-button v-if="row.role !== 'captain' && row.role !== 'owner'" type="danger" size="small" link @click="handleRemoveMember(row)">移除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <template #footer>
        <el-button @click="memberDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 战队详情弹窗 -->
    <el-dialog :title="`战队详情 - ${detailTeam.name || ''}`" v-model="detailDialogVisible" width="900px" top="5vh">
      <div v-loading="detailLoading" class="team-detail-content">
        <!-- 基本信息 -->
        <el-descriptions :column="3" border size="small" class="detail-desc">
          <el-descriptions-item label="战队ID">{{ detailTeam.id }}</el-descriptions-item>
          <el-descriptions-item label="运动类型">{{ getSportTypeLabel(detailTeam.sportType || detailTeam.sport) }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusTagType(detailTeam.status)" size="small">{{ getStatusLabel(detailTeam.status) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="队长">{{ detailTeam.leader?.nickname || '未知' }}</el-descriptions-item>
          <el-descriptions-item label="成员数">{{ detailTeam.memberCount || 0 }}/{{ detailTeam.maxMembers || 12 }}</el-descriptions-item>
          <el-descriptions-item label="奖金分配">
            <el-tag size="small" :type="detailTeam.prizeDistributionMethod === 'average_all' ? 'success' : 'warning'">
              {{ detailTeam.prizeDistributionMethod === 'average_all' ? '平均分配' : '队长收取' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ formatDateTime(detailTeam.createTime) }}</el-descriptions-item>
          <el-descriptions-item label="描述" :span="2">{{ detailTeam.description || '暂无描述' }}</el-descriptions-item>
        </el-descriptions>

        <!-- 比赛统计概览 -->
        <div class="detail-stats-row" v-if="detailTeam.matchStats">
          <div class="detail-stat-item">
            <div class="detail-stat-value">{{ detailTeam.matchStats.total || 0 }}</div>
            <div class="detail-stat-label">总比赛</div>
          </div>
          <div class="detail-stat-item">
            <div class="detail-stat-value">{{ detailTeam.matchStats.completed || 0 }}</div>
            <div class="detail-stat-label">已完成</div>
          </div>
          <div class="detail-stat-item">
            <div class="detail-stat-value" style="color: #67C23A;">{{ detailTeam.matchStats.won || 0 }}</div>
            <div class="detail-stat-label">胜场</div>
          </div>
          <div class="detail-stat-item">
            <div class="detail-stat-value" style="color: #409EFF;">{{ detailTeam.matchStats.winRate || 0 }}%</div>
            <div class="detail-stat-label">胜率</div>
          </div>
          <div class="detail-stat-item">
            <div class="detail-stat-value" style="color: #E6A23C;">{{ detailTeam.honorCount || 0 }}</div>
            <div class="detail-stat-label">荣誉数</div>
          </div>
          <div class="detail-stat-item">
            <div class="detail-stat-value" style="color: #F56C6C;">{{ detailTeam.activityCount || 0 }}</div>
            <div class="detail-stat-label">参赛活动</div>
          </div>
        </div>

        <!-- Tab 页签 -->
        <el-tabs v-model="detailActiveTab" class="detail-tabs">
          <!-- 成员 -->
          <el-tab-pane label="成员" name="members">
            <el-table :data="detailTeam.members || []" stripe size="small" style="width: 100%">
              <el-table-column label="成员" min-width="180">
                <template #default="{ row }">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <el-avatar :size="32" :src="row.avatar" />
                    <span>{{ row.nickname }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column label="角色" width="100">
                <template #default="{ row }">
                  <el-tag :type="(row.role === 'captain' || row.role === 'owner') ? 'danger' : 'info'" size="small">
                    {{ (row.role === 'captain' || row.role === 'owner') ? '队长' : row.role === 'admin' ? '管理员' : '队员' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="加入时间" width="170">
                <template #default="{ row }">
                  {{ row.joinTime ? row.joinTime.replace('T', ' ').substring(0, 19) : '-' }}
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- 荣誉 -->
          <el-tab-pane label="荣誉" name="honors">
            <el-table :data="detailTeam.honors || []" stripe size="small" style="width: 100%">
              <el-table-column label="荣誉" min-width="200">
                <template #default="{ row }">
                  <span>{{ row.honor_level === 1 ? '🏆' : row.honor_level === 2 ? '🥈' : row.honor_level === 3 ? '🥉' : '🎖️' }} {{ row.honor_name }}</span>
                </template>
              </el-table-column>
              <el-table-column label="赛事活动" min-width="180">
                <template #default="{ row }">{{ row.activity_title || '-' }}</template>
              </el-table-column>
              <el-table-column label="获得时间" width="170">
                <template #default="{ row }">{{ row.obtain_time ? row.obtain_time.replace('T', ' ').substring(0, 19) : '-' }}</template>
              </el-table-column>
              <el-table-column label="描述" min-width="150">
                <template #default="{ row }">{{ row.description || '-' }}</template>
              </el-table-column>
              <template #empty>
                <el-empty description="暂无荣誉记录" :image-size="60" />
              </template>
            </el-table>
          </el-tab-pane>

          <!-- 比赛记录 -->
          <el-tab-pane label="比赛记录" name="matches">
            <el-table :data="detailTeam.matches || []" stripe size="small" style="width: 100%">
              <el-table-column label="对阵" min-width="200">
                <template #default="{ row }">
                  <span>{{ row.team_a_name || '未知' }} VS {{ row.team_b_name || '未知' }}</span>
                </template>
              </el-table-column>
              <el-table-column label="比分" width="120">
                <template #default="{ row }">
                  <span v-if="row.status === 'completed'">{{ row.team_a_score || 0 }} : {{ row.team_b_score || 0 }}</span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column label="结果" width="80">
                <template #default="{ row }">
                  <el-tag v-if="row.status === 'completed'" size="small" :type="getMatchResultType(row)" effect="light">
                    {{ getMatchResultLabel(row) }}
                  </el-tag>
                  <el-tag v-else size="small" type="info">{{ row.status === 'in_progress' ? '进行中' : '待开始' }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="比赛时间" width="160">
                <template #default="{ row }">{{ formatDateTime(row.start_time) }}</template>
              </el-table-column>
              <template #empty>
                <el-empty description="暂无比赛记录" :image-size="60" />
              </template>
            </el-table>
          </el-tab-pane>

          <!-- 参赛活动 -->
          <el-tab-pane label="参赛活动" name="activities">
            <el-table :data="detailTeam.activities || []" stripe size="small" style="width: 100%">
              <el-table-column label="活动名称" min-width="200">
                <template #default="{ row }">{{ row.title || row.activityTitle || '-' }}</template>
              </el-table-column>
              <el-table-column label="参赛类型" width="100">
                <template #default="{ row }">
                  <el-tag size="small" :type="row.participant_type === 'team' ? 'warning' : 'primary'">
                    {{ row.participant_type === 'team' ? '战队' : '个人' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="报名时间" width="170">
                <template #default="{ row }">{{ formatDateTime(row.joinTime) }}</template>
              </el-table-column>
              <el-table-column label="状态" width="100">
                <template #default="{ row }">
                  <el-tag size="small" :type="getActivityStatusType(row.status)">{{ getActivityStatusLabel(row.status) }}</el-tag>
                </template>
              </el-table-column>
              <template #empty>
                <el-empty description="暂无参赛活动记录" :image-size="60" />
              </template>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </div>
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import api from '@/utils/api'

export default {
  name: 'TeamManagement',
  
  setup() {
    const loading = ref(false)
    const dialogVisible = ref(false)
    const memberDialogVisible = ref(false)
    const memberLoading = ref(false)
    const formRef = ref(null)
    const isEditMode = ref(false)
    const teamList = ref([])
    const memberList = ref([])
    const memberTeam = ref({})
    
    // 详情弹窗
    const detailDialogVisible = ref(false)
    const detailLoading = ref(false)
    const detailTeam = ref({})
    const detailActiveTab = ref('members')
    
    // 筛选参数
    const filterParams = reactive({
      status: '',
      sportType: '',
      createDateRange: [],
      keyword: ''
    })
    
    // 分页参数
    const pagination = reactive({
      current: 1,
      size: 10,
      total: 0
    })
    
    // 表单数据
    const formData = reactive({
      id: null,
      name: '',
      sportType: '',
      maxMembers: 20,
      description: '',
      avatar: '',
      avatarFile: null,
      prize_distribution_method: 'captain_only'
    })
    
    const formRules = {
      name: [
        { required: true, message: '请输入战队名称', trigger: 'blur' },
        { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
      ],
      sportType: [
        { required: true, message: '请选择运动类型', trigger: 'change' }
      ],
      maxMembers: [
        { required: true, message: '请输入最大成员数量', trigger: 'blur' }
      ]
    }
    
    const teamStats = ref([
      { title: '总战队数', value: '0', icon: 'Trophy', color: '#409EFF' },
      { title: '今日新增', value: '0', icon: 'Plus', color: '#67C23A' },
      { title: '活跃战队', value: '0', icon: 'TrendCharts', color: '#E6A23C' },
      { title: '已满员', value: '0', icon: 'User', color: '#F56C6C' }
    ])
    
    const dialogTitle = computed(() => isEditMode.value ? '编辑战队' : '创建战队')
    
    // 加载战队列表
    const loadTeams = async () => {
      loading.value = true
      try {
        const params = {
          page: pagination.current,
          pageSize: pagination.size,
          status: filterParams.status,
          sportType: filterParams.sportType,
          keyword: filterParams.keyword
        }
        if (filterParams.createDateRange && filterParams.createDateRange.length === 2) {
          params.startDate = filterParams.createDateRange[0]
          params.endDate = filterParams.createDateRange[1]
        }
        const response = await api.getTeams(params)
        teamList.value = response.data.teams
        pagination.total = response.data.pagination.total
        const stats = response.data.stats || {}
        teamStats.value = [
          { title: '总战队数', value: (stats.total ?? response.data.pagination.total ?? 0).toString(), icon: 'Trophy', color: '#409EFF' },
          { title: '今日新增', value: (stats.todayNew || 0).toString(), icon: 'Plus', color: '#67C23A' },
          { title: '活跃战队', value: (stats.active || 0).toString(), icon: 'TrendCharts', color: '#E6A23C' },
          { title: '已满员', value: (stats.full || 0).toString(), icon: 'User', color: '#F56C6C' }
        ]
      } catch (error) {
        console.error('加载战队列表失败:', error?.response?.data || error?.message || error)
        ElMessage.error('加载战队列表失败: ' + (error?.response?.data?.message || error?.message || '未知错误'))
      } finally {
        loading.value = false
      }
    }
    
    const handleFilterChange = () => { pagination.current = 1; loadTeams() }
    const handleSearch = () => { pagination.current = 1; loadTeams() }
    const resetFilters = () => {
      Object.assign(filterParams, { status: '', sportType: '', createDateRange: [], keyword: '' })
      pagination.current = 1
      loadTeams()
    }
    const handleSizeChange = (size) => { pagination.size = size; pagination.current = 1; loadTeams() }
    const handleCurrentChange = (current) => { pagination.current = current; loadTeams() }
    
    // 查看战队详情 - 从后端获取完整数据
    const handleViewTeam = async (team) => {
      detailTeam.value = { ...team }
      detailActiveTab.value = 'members'
      detailDialogVisible.value = true
      detailLoading.value = true
      try {
        const response = await api.getTeamInfo(team.id)
        if (response.success && response.data) {
          detailTeam.value = response.data
        }
      } catch (error) {
        console.error('获取战队详情失败:', error)
        // 降级使用列表中的数据
      } finally {
        detailLoading.value = false
      }
    }
    
    const handleCreateTeam = () => {
      isEditMode.value = false
      resetForm()
      dialogVisible.value = true
    }
    
    const handleEditTeam = async (team) => {
      isEditMode.value = true
      Object.assign(formData, {
        id: team.id,
        name: team.name,
        sportType: team.sportType || team.sport,
        maxMembers: team.maxMembers,
        description: team.description || '',
        avatar: team.avatar || team.logo || '',
        avatarFile: null,
        prize_distribution_method: team.prize_distribution_method || team.prizeDistributionMethod || 'captain_only'
      })
      dialogVisible.value = true
    }
    
    const handleToggleStatus = async (team) => {
      try {
        await api.toggleTeamStatus(team.id)
        ElMessage.success('战队状态切换成功')
        loadTeams()
      } catch (error) {
        console.error('切换战队状态失败:', error)
        ElMessage.error('切换战队状态失败')
      }
    }
    
    const handleManageMembers = async (team) => {
      memberTeam.value = team
      memberDialogVisible.value = true
      await loadMembers(team.id)
    }
    
    const loadMembers = async (teamId) => {
      memberLoading.value = true
      try {
        const response = await api.getTeamMembers(teamId)
        memberList.value = response.data || []
      } catch (error) {
        console.error('获取成员列表失败:', error)
        ElMessage.error('获取成员列表失败')
        memberList.value = []
      } finally {
        memberLoading.value = false
      }
    }
    
    const handleRemoveMember = async (member) => {
      try {
        await ElMessageBox.confirm(`确定要移除成员"${member.nickname}"吗？`, '确认移除', { type: 'warning' })
        await api.removeTeamMember(memberTeam.value.id, member.userId)
        ElMessage.success('成员已移除')
        await loadMembers(memberTeam.value.id)
        loadTeams()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('移除成员失败:', error)
          ElMessage.error('移除成员失败')
        }
      }
    }

    const handleAvatarChange = (file) => {
      formData.avatarFile = file.raw
      formData.avatar = URL.createObjectURL(file.raw)
    }
    
    const handleSubmit = async () => {
      if (!formRef.value) return
      try {
        await formRef.value.validate()
        if (isEditMode.value) {
          // 如果有新头像文件，先上传
          if (formData.avatarFile) {
            const uploadRes = await api.uploadTeamLogo(formData.id, formData.avatarFile)
            if (uploadRes.success) {
              formData.avatar = uploadRes.data.logo || uploadRes.data.avatar
            }
          }
          // 提交表单数据（不含文件对象）
          const { avatarFile, ...submitData } = formData
          await api.updateTeam(formData.id, submitData)
          ElMessage.success('战队更新成功')
        } else {
          await api.createTeam(formData)
          ElMessage.success('战队创建成功')
        }
        dialogVisible.value = false
        loadTeams()
      } catch (error) {
        console.error('表单提交失败:', error)
        ElMessage.error(isEditMode.value ? '战队更新失败' : '战队创建失败')
      }
    }
    
    const handleDialogClose = (done) => {
      ElMessageBox.confirm('确定要关闭吗？未保存的更改将会丢失')
        .then(() => { done(); resetForm() })
        .catch(() => {})
    }
    
    const resetForm = () => {
      Object.assign(formData, {
        id: null, name: '', sportType: '', maxMembers: 20, description: '', avatar: '', prize_distribution_method: 'captain_only'
      })
    }
    
    // 工具函数
    const getSportTypeLabel = (type) => {
      const labels = { basketball: '篮球', football: '足球', badminton: '羽毛球', tabletennis: '乒乓球', tennis: '网球', fishing: '钓鱼', pingpong: '乒乓球' }
      return labels[type] || type
    }
    const getSportTypeTag = (type) => {
      const tags = { basketball: 'primary', football: 'success', badminton: 'warning', tabletennis: 'info', tennis: 'danger', fishing: 'info', pingpong: 'info' }
      return tags[type] || ''
    }
    const getStatusLabel = (status) => {
      const labels = { active: '活跃', inactive: '未激活', full: '已满员' }
      return labels[status] || status
    }
    const getStatusTagType = (status) => {
      const types = { active: 'success', inactive: 'info', full: 'warning' }
      return types[status] || ''
    }
    const formatDateTime = (datetime) => {
      if (!datetime) return ''
      return datetime.replace('T', ' ').substring(0, 19)
    }

    // 比赛结果判定
    const getMatchResultType = (match) => {
      const teamId = detailTeam.value.id
      if (match.team_a_team_id == teamId) {
        return match.team_a_score > match.team_b_score ? 'success' : match.team_a_score < match.team_b_score ? 'danger' : 'info'
      } else if (match.team_b_team_id == teamId) {
        return match.team_b_score > match.team_a_score ? 'success' : match.team_b_score < match.team_a_score ? 'danger' : 'info'
      }
      return 'info'
    }
    const getMatchResultLabel = (match) => {
      const teamId = detailTeam.value.id
      if (match.team_a_team_id == teamId) {
        return match.team_a_score > match.team_b_score ? '胜' : match.team_a_score < match.team_b_score ? '负' : '平'
      } else if (match.team_b_team_id == teamId) {
        return match.team_b_score > match.team_a_score ? '胜' : match.team_b_score < match.team_a_score ? '负' : '平'
      }
      return '-'
    }

    // 活动状态
    const getActivityStatusType = (status) => {
      const types = { upcoming: 'primary', registering: 'success', in_progress: 'warning', completed: 'info', cancelled: 'danger' }
      return types[status] || 'info'
    }
    const getActivityStatusLabel = (status) => {
      const labels = { upcoming: '即将开始', registering: '报名中', in_progress: '进行中', completed: '已结束', cancelled: '已取消' }
      return labels[status] || status || '-'
    }
    
    onMounted(() => { loadTeams() })
    
    return {
      loading, dialogVisible, memberDialogVisible, memberLoading, formRef, isEditMode,
      teamList, memberList, memberTeam, filterParams, pagination, formData, formRules, teamStats, dialogTitle,
      detailDialogVisible, detailLoading, detailTeam, detailActiveTab,
      handleFilterChange, handleSearch, resetFilters, handleSizeChange, handleCurrentChange,
      handleViewTeam, handleCreateTeam, handleEditTeam, handleToggleStatus,
      handleManageMembers, handleRemoveMember, handleAvatarChange, handleSubmit, handleDialogClose,
      getSportTypeLabel, getSportTypeTag, getStatusLabel, getStatusTagType, formatDateTime,
      getMatchResultType, getMatchResultLabel, getActivityStatusType, getActivityStatusLabel
    }
  }
}
</script>

<style scoped>
.team-management { padding: 20px; }
.page-header, .filter-section, .stats-section, .list-section, .table-header, .pagination-section { margin-bottom: 24px; }
.stat-card, .filter-card { border: none; }
.stat-content { display: flex; align-items: center; gap: 16px; }
.stat-icon { font-size: 24px; padding: 12px; border-radius: 8px; background: rgba(64, 158, 255, 0.1); }
.stat-info { flex: 1; }
.stat-value { font-size: 24px; font-weight: 600; color: #303133; margin-bottom: 4px; }
.stat-title { font-size: 14px; color: #909399; }
.team-info { display: flex; align-items: center; gap: 12px; }
.team-details { display: flex; flex-direction: column; }
.team-name { font-weight: 500; color: #303133; }
.team-desc { font-size: 12px; color: #909399; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.member-count .text-danger { color: #f56c6c; }
.expand-content { padding: 16px; background: #f8f9fa; border-radius: 8px; }
.members-section { margin-top: 16px; padding-top: 16px; border-top: 1px solid #e6e8eb; }
.members-section h4 { margin-bottom: 12px; color: #303133; }
.member-item { display: flex; align-items: center; gap: 8px; padding: 8px; background: #fff; border-radius: 6px; border: 1px solid #e6e8eb; margin-bottom: 8px; }
.member-info { flex: 1; }
.member-name { font-size: 12px; font-weight: 500; color: #303133; }
.member-role { margin-top: 2px; }

/* 详情弹窗样式 */
.team-detail-content { min-height: 200px; }
.detail-desc { margin-bottom: 20px; }
.detail-stats-row {
  display: flex; gap: 0; margin-bottom: 20px;
  background: #f5f7fa; border-radius: 8px; overflow: hidden;
}
.detail-stat-item {
  flex: 1; text-align: center; padding: 16px 8px;
  border-right: 1px solid #e6e8eb;
}
.detail-stat-item:last-child { border-right: none; }
.detail-stat-value { font-size: 24px; font-weight: 600; color: #303133; }
.detail-stat-label { font-size: 12px; color: #909399; margin-top: 4px; }
.detail-tabs { margin-top: 8px; }

@media (max-width: 768px) {
  .team-info { flex-direction: column; text-align: center; gap: 8px; }
  .team-details { align-items: center; }
  .member-item { flex-direction: column; text-align: center; }
  .detail-stats-row { flex-wrap: wrap; }
  .detail-stat-item { flex: 1 1 33%; border-bottom: 1px solid #e6e8eb; }
}
</style>